/**
 * @typedef {import ('../Documents/Document').default} Document
 * @typedef {import ('./ListDocumentsProps').default} Props
 * @typedef {import ('./ListDocumentsState').default} State
 */
import { AuthContext } from 'shared/AuthProvider';
import { object } from '@mooveguru/js-utilities';
import { translate } from '../../Internationalization';
import {
	withDocumentsService,
	withPropertyService,
} from '../../../service-container';
import { withProperty } from '../PropertyProvider';
import BaseHero from '../../../shared/BaseHero';
import captureError from '../../../utils/captureError';
import categoryList from '@mooveguru/yhh-shared-config/document-categories.json';
import DocumentCategoryList from './DocumentCategoryList';
import downloadBlob from '../../../utils/downloadBlob';
import fileTypes from '../../../config/local/file-types';
import FormErrorMessages from '../../../shared/Forms/Messages/FormErrorMessages';
import FormSuccessMessages from '../../../shared/Forms/Messages/FormSuccessMessages';
import images from '../../../config/local/images';
import Loading from '../../../shared/Loading';
import React from 'react';

/** @extends {React.Component<Props, State>} */
export class ListDocuments extends React.Component {
	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			documentErrorMessage: null,
			documents: {},
			errorMessage: null,
			isGettingData: true,
		};

		this.deleteDocument = this.deleteDocument.bind(this);
		this.downloadDocument = this.downloadDocument.bind(this);
	}

	/**
	 * @param {unknown} error
	 * @returns {void}
	 */
	#error(error) {
		captureError(error);

		this.setState({ errorMessage: translate('global.error') });
	}

	/**
	 * @param {string} documentId
	 */
	async deleteDocument(documentId) {
		try {
			const propertyId = this.#getPropertyId();

			if (!propertyId) {
				throw new Error(translate('global.error'));
			}

			await this.props.propertyService.deletePropertyDocument(
				propertyId,
				documentId
			);

			this.setState((prevState) => {
				const documents = { ...prevState.documents };
				const entries = Object.entries(documents);

				for (const [category, categoryDocuments] of entries) {
					if (!Array.isArray(categoryDocuments)) {
						continue;
					}
					const filteredDocuments = [];

					for (const categoryDocument of categoryDocuments) {
						if (categoryDocument.id !== documentId) {
							filteredDocuments.push(categoryDocument);
						}
					}

					documents[category] = filteredDocuments;
				}

				return { documents };
			});
		} catch (error) {
			this.#error(error);
		}
	}

	/**
	 *  @param {Props} prevProps
	 *  @returns {void}
	 */
	componentDidUpdate(prevProps) {
		if (this.#getPropertyId() !== ListDocuments.getPropertyId(prevProps)) {
			this.setState({
				documents: {},
				errorMessage: null,
				isGettingData: true,
			});

			this.loadDocuments();
			return;
		}

		if (this.props.match?.params?.propertyId) {
			return;
		}

		/*
		 * This checks if the previous property data from PropertyProvider is the same as current property data.
		 * If they are not equal then update local state with new property data.
		 */
		if (
			JSON.stringify(this.props.property?.selectedProperty) ===
			JSON.stringify(prevProps.property?.selectedProperty)
		) {
			return;
		}

		const documentList = ListDocuments.#sortDocuments(
			this.props.property?.selectedProperty?.documents
		);

		this.setState({
			documents: documentList,
			errorMessage: null,
			isGettingData: false,
		});
	}

	/** @returns {Promise<void>} */
	async componentDidMount() {
		await this.loadDocuments();
	}

	/**
	 * @param {Props} props
	 * @returns {string | undefined}
	 */
	static getPropertyId(props) {
		return (
			props.match?.params?.propertyId ??
			props.property?.selectedProperty?.id
		);
	}

	/** @returns {string | undefined} */
	#getPropertyId() {
		return ListDocuments.getPropertyId(this.props);
	}

	/** @returns {Promise<void>} */
	async loadDocuments() {
		const propertyId = this.#getPropertyId();

		if (!propertyId) {
			this.setState({
				documents: {},
				errorMessage: null,
				isGettingData: false,
			});

			return;
		}

		try {
			const routeProperty = this.props.match?.params?.propertyId
				? await this.props.propertyService.getPropertyById(propertyId)
				: null;
			const documents =
				routeProperty?.documents ??
				this.props.property?.selectedProperty?.documents;
			const propertyDocumentList =
				ListDocuments.#sortDocuments(documents);

			this.setState({
				documents: propertyDocumentList,
				errorMessage: null,
				isGettingData: false,
			});
		} catch (error) {
			this.#error(error);
			this.setState({
				documents: {},
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {{ [key: string]: Document[] }} left
	 * @param {{ [key: string]: Document[] }} right
	 * @returns {{ [key: string]: Document[] }}
	 */
	static #mergeGroupedDocuments(left, right) {
		const merged = { ...(left ?? {}) };

		Object.entries(right ?? {}).forEach(([category, docs]) => {
			const existing = merged[category] ?? [];
			const deduped = new Map(existing.map((doc) => [doc.id, doc]));

			(docs ?? []).forEach((doc) => {
				deduped.set(doc.id, doc);
			});

			merged[category] = Array.from(deduped.values());
		});

		return merged;
	}

	/**
	 * @param {string} documentId
	 * @param {string} title
	 */
	async downloadDocument(documentId, title) {
		const response =
			await this.props.documentsService.getDocumentDownloadLink(
				documentId
			);

		if (!response) {
			return;
		}

		downloadBlob(response, title);
	}

	/**
	 * @template {(Document & { id: string }) } Type
	 * @param {Type[] | undefined} documents
	 * @returns {{ [key: Type['category']]: Document[] }}
	 */
	static #sortDocuments(documents) {
		if (!documents) {
			return [];
		}

		const unassignedKey = Object.keys(categoryList)[0];
		const unassignedValue = Object.values(categoryList)[0];

		const documentList = documents.map((document) => ({
			/* eslint-disable sort-keys -- grouped mapping for readability */
			category:
				// @ts-ignore Falls back to default if key doesn't exist
				categoryList[document.category ?? unassignedKey] ??
				unassignedValue,
			createdAt: (() => {
				if (document.createdAt) {
					return new Date(document.createdAt);
				}

				if (document.created_at) {
					return new Date(document.created_at);
				}

				return null;
			})(),
			id: document.id ?? document._id,
			src: document.src,
			title: document.title,
			// @ts-ignore Falls back to default if key doesn't exist
			type: fileTypes[document.src?.split('.').pop() ?? ''] || null,
			/* eslint-enable sort-keys */
		}));

		documentList.sort(
			/**
			 *
			 * @param {Document} a
			 * @param {Document} b
			 * @returns {-1|0|1}
			 */
			(a, b) => {
				const comparison =
					a.category?.localeCompare(b?.category ?? '') ?? 0;

				if (comparison === 0) {
					return 0;
				}

				return comparison >= 1 ? 1 : -1;
			}
		);

		return object.groupBy(documentList, 'category');
	}

	render() {
		if (this.state.isGettingData) {
			return <Loading />;
		}

		return (
			<React.Fragment>
				<div className="pb-6">
					<BaseHero
						heroImageUrl={images.homeowners.documents.header}
					/>
				</div>

				{this.state.errorMessage ? (
					<FormErrorMessages messages={[this.state.errorMessage]} />
				) : null}
				{this.props.location?.state?.message ? (
					<FormSuccessMessages
						messages={this.props.location.state.message}
					/>
				) : null}

				<section className="listing listing-col-size-2">
					<DocumentCategoryList
						categories={this.state.documents}
						deleteDocument={this.deleteDocument}
						downloadOnClick={this.downloadDocument}
						match={this.props.match}
						selectedPropertyId={this.#getPropertyId()}
					/>
				</section>

				<FormErrorMessages messages={this.state.documentErrorMessage} />
			</React.Fragment>
		);
	}
}

ListDocuments.contextType = AuthContext;

// @ts-ignore, HOC type needs to be fixed
export default withDocumentsService(
	withPropertyService(withProperty(ListDocuments))
);
